<script lang="ts" setup>


// ─── IMPORTS ────────────────────────────────────────────────────────────────
import { User } from './models/user'
import { Company } from './models/company'
import { ref, onMounted, computed, watch } from 'vue'

import { useUserStore } from './store'

import {
  validateEmail,
  validatePasswords,
  validationRut,
  generatePassword
} from './helpers'


// ─── STORES ────────────────────────────────────────────────────────────────
const userStore = useUserStore()

// ─── REFS: UI Flags and Data ───────────────────────────────────────────────
const loading = ref(false)
const activeEditUser = ref(false)
const activeCreateNewUser = ref(false)
const loadingCreateUser = ref(false)
const loadingEditUser = ref(false)
const searchUsers = ref('')
const page = ref(1)
const itemsPerPage = 10
const showPassword = ref(false)
const formTouched = ref(false)
const formRef = ref()
const formValid = ref(false)
const headers = [
  { title: 'NOMBRE', key: 'name' },
  { title: 'EMAIL', key: 'email' },
  { title: 'EMPRESA', key: 'company' },
  { title: 'ROL', key: 'role' },
  { title: 'ESTADO', key: 'status' },
  { title: 'ACCIONES', key: 'actions' },
]

// ─── REFS: Usuario ──────────────────────────────────────────────────────
const users = ref<User[]>([])
const user = ref<User>(new User())
const companies = ref<Company[]>([])
const password = ref('')
const confirmPassword = ref('')
const generateRandomPassword = ref(false)
const sendEmailPassword = ref(true)

const rut = ref('')
const rutError = ref('')

// ─── ALERTAS ───────────────────────────────────────────────────────────────
const activeAlert = ref(false)
const textAlert = ref('')
const colorAlert = ref('error')

// ─── VALIDACIONES ──────────────────────────────────────────────────────────
const emailError = ref('')
const passwordError = ref('')





// ─── COMPUTED ──────────────────────────────────────────────────────────────
const pageCount = computed(() => Math.ceil(users.value.length / itemsPerPage))

// Función para obtener el nombre de la empresa por ID
const getCompanyName = (companyId: string): string => {
  if (!companyId) return 'Sin empresa'
  const company = companies.value.find(c => c.id === companyId)
  return company ? company.name : 'Empresa no encontrada'
}

// ─── FUNCIONES AUXILIARES ──────────────────────────────────────────────────

// Función para cargar empresas
const getCompanies = async () => {
  try {
    companies.value = await userStore.getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al cargar las empresas. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  }
}
// randon clave


// watcher para el checkbox de generar contraseña
watch(generateRandomPassword, (newVal) => {
  if (newVal) {
    const pwd = generatePassword()
    password.value = pwd
    confirmPassword.value = pwd
  } else {
    password.value = ''
    confirmPassword.value = ''
  }
})

// watcher para auto-generar folderName
watch([() => user.value.firstName, () => user.value.lastName], ([firstName, lastName]) => {
  if (firstName && lastName && !user.value.folderName) {
    const folderName = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`
      .replace(/[^a-z0-9_]/gi, '') // eliminar caracteres especiales
      .replace(/\s+/g, '_') // reemplazar espacios con guiones bajos
    user.value.folderName = folderName
  }
})

const resetForm = () => {
  user.value = new User()
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  sendEmailPassword.value = true
  emailError.value = ''
  passwordError.value = ''
  rut.value = ''
  rutError.value = ''
  formTouched.value = false
}
const getStatusLabel = (role: string) => {
  switch (role) {
    case 'base':
      return 'Aplicado'
    case 'admin':
      return 'Profesional'
    case 'inactive':
      return 'Rechazado'
    default:
      return 'Desconocido'
  }
}

const getStatusColor = (role: string) => {
  switch (role) {
    case 'base':
      return 'blue'
    case 'admin':
      return 'green'
    case 'inactive':
      return 'red'
    default:
      return 'grey'
  }
}

// ─── EVENTOS / BOTONES ─────────────────────────────────────────────────────

const getUsers = async () => {
  loading.value = true
  try {
    users.value = await userStore.getCustomers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al cargar los clientes. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loading.value = false
  }
}

const btnSetActiveCreateUser = () => {
  activeCreateNewUser.value = !activeCreateNewUser.value
  user.value = new User()
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  emailError.value = ''
  passwordError.value = ''
}

const createUser = async () => {
  await userStore.postUser({
    email: user.value.email,
    password: password.value,
    name: user.value.name,
    user: user.value.toPlainObject(),
    sendEmailPassword: sendEmailPassword.value,
  })
}

const validateForm = (): boolean => {
  let isValid = true

  // Validación de campos obligatorios
  if (!user.value.firstName || !user.value.lastName || !user.value.email || !user.value.folderName || !user.value.companyId) {
    activeAlert.value = true
    textAlert.value = 'Todos los campos obligatorios son requeridos'
    colorAlert.value = 'error'
    isValid = false
  }

  // Validación de email
  if (!user.value.email) {
    emailError.value = 'Email requerido'
    isValid = false
  } else if (!validateEmail(user.value.email)) {
    emailError.value = 'Email no válido'
    isValid = false
  }
  
  // Validación de empresa
  if (!user.value.companyId) {
    activeAlert.value = true
    textAlert.value = 'Debe seleccionar una empresa'
    colorAlert.value = 'error'
    isValid = false
  }
  
  // Validación de rut
  if (!rut.value) {
    rutError.value = 'Rut requerido'
    isValid = false
  } else if (!validationRut(rut.value)) {
    rutError.value = 'Rut inválido'
    isValid = false
  }
  // Validación de contraseñas

  const pwdValidation = validatePasswords(password.value, confirmPassword.value, generateRandomPassword.value)
  if (pwdValidation) {
    passwordError.value = pwdValidation
    isValid = false
    activeAlert.value = true
    textAlert.value = pwdValidation
    colorAlert.value = 'error'
  }

  return isValid
}

const btnSetActiveEditUser = async (selectedUser: User) => {
  user.value = new User(selectedUser)
  // Limpiar campos de contraseña en modo edición
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  passwordError.value = ''
  emailError.value = ''
  rutError.value = ''
  activeEditUser.value = true
}

const btnCreateUser = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  user.value.name = user.value.firstName + ' ' + user.value.lastName
  user.value.type = 'customer'
  user.value.role = 'base'
  user.value.active = true

  loadingCreateUser.value = true
  try {
    await createUser()
    activeAlert.value = true
    textAlert.value = 'Cliente creado exitosamente'
    colorAlert.value = 'success'
    activeCreateNewUser.value = false
    resetForm()
    await getUsers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al crear el cliente. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loadingCreateUser.value = false
  }
}

const btnUpdateUser = async () => {
  user.value.name = user.value.firstName + ' ' + user.value.lastName
  user.value.type = 'customer'
  user.value.role = 'base'
  user.value.active = true

  loadingEditUser.value = true
  try {
    await userStore.putUser(user.value)
    activeAlert.value = true
    textAlert.value = 'Cliente actualizado exitosamente'
    colorAlert.value = 'success'
    activeEditUser.value = false
    await getUsers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al actualizar el cliente. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loadingEditUser.value = false
  }
}

const btnSendPasswordResetLink = async (email: string) => {
  try {
    await userStore.postSendPasswordResetLink(email)
    activeAlert.value = true
    textAlert.value = 'Enlace de restablecimiento enviado correctamente'
    colorAlert.value = 'success'
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al enviar el enlace de restablecimiento'
    colorAlert.value = 'error'
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────────────────────────

onMounted(async () => {
  // Cargar empresas y usuarios reales
  await getCompanies()
  await getUsers()
})

</script>

<template>
  <v-card elevation="0" class="bg-background pa-5 rounded-lg">

    <div v-if="!activeCreateNewUser && !activeEditUser">

      <v-card class=" mt-3 rounded-lg " elevation="3">
        <div class="rounded-lg justify-space-between mb-4 pa-2 d-flex">
          <v-card-title color="primary" class="text-subtitle-4">Clientes </v-card-title>
          <v-spacer />
          <v-btn v-if="!activeCreateNewUser && !activeEditUser" @click="btnSetActiveCreateUser" variant="text" text="Nuevo
            cliente " />
          <v-btn v-else @click="(activeCreateNewUser = false), (activeEditUser = false)" variant="text"
            text="Volver a los clientes" />
        </div>
        <v-text-field v-model="searchUsers" append-inner-icon="mdi-magnify" density="compact" label="Buscar"
          variant="solo" hide-details single-line color="surface-light" class="mx-4" bg-color="background" />
        <v-data-table class=" rounded-lg text-body-2" v-model:page="page" :headers="headers"
          :items="users" :search="searchUsers" :loading="loading" :items-per-page="itemsPerPage">
          <template v-slot:loading>
            <v-skeleton-loader class="bg-background " type="table-row@10" />
          </template>
          <template #[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="36" class="mr-3" color="primary">
                <span class="text-white text-subtitle-2 font-weight-bold">
                  {{ item.name?.substring(0, 2).toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.role }}</div>
              </div>
            </div>
          </template>
          <template #[`item.company`]="{ item }">
            <div class="text-body-2">
              {{ getCompanyName(item.companyId) }}
            </div>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip :color="getStatusColor(item.role)" class="text-caption" size="small" variant="tonal">
              {{ getStatusLabel(item.role) }}
            </v-chip>
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="d-flex gap-1">
              <v-btn 
                variant="text" 
                @click="btnSetActiveEditUser(item)" 
                icon="mdi-pencil-outline"
                v-tooltip="`Editar`"
              ></v-btn>
              <v-btn 
                variant="text" 
                @click="btnSendPasswordResetLink(item.email)" 
                icon="mdi-lock-reset"
                v-tooltip="`Restablecer contraseña`"
                color="orange"
              ></v-btn>
            </div>
          </template>
          <template v-slot:bottom>
            <div class="text-center pt-2">
              <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
    <v-row v-if="activeCreateNewUser || activeEditUser" justify="center">
      <v-col cols="12" md="10" lg="8">
        <div>
          <v-card elevation="4" class="bg-background pa-0 ma-0 rounded-lg content-container">
            <!-- Sticky Action Bar  -->

            <div class="sticky-header bg-grey-lighten-4
 d-flex justify-space-between align-center px-4 py-3 " style="border-bottom: 1px solid #e0e0e0;">
              <span class="text-h6 font-weight-medium">{{ activeEditUser ? 'Editar cliente' : 'Crear nuevo cliente' }}</span>
              <div>
                <v-btn color="red" variant="tonal" class="mr-2" rounded="lg" @click="resetForm">Limpiar</v-btn>
                <v-btn color="primary" variant="tonal" class="mr-2" rounded="lg"
                  @click="activeCreateNewUser = false, activeEditUser = false">Volver</v-btn>
                <v-btn 
                  v-if="activeCreateNewUser" 
                  color="primary" 
                  class="text-white" 
                  rounded="lg" 
                  @click="btnCreateUser"
                  :loading="loadingCreateUser"
                >
                  Crear
                </v-btn>
                <v-btn 
                  v-if="activeEditUser" 
                  color="primary" 
                  class="text-white" 
                  rounded="lg" 
                  @click="btnUpdateUser"
                  :loading="loadingEditUser"
                >
                  Actualizar
                </v-btn>
              </div>
            </div>

            <!-- Main Content -->

            <v-row justify="center">
              <v-col cols="12">
                <v-sheet color="white" class="pa-6 " elevation="0">
                  <v-form ref="formRef" v-model="formValid" validate-on="submit lazy">

                    <!-- Main Content (dentro del mismo v-sheet) -->
                    <div class="pa-6">
                      <h4 class="text-h6 mb-6">1. Información del usuario</h4>

                      <v-row>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="user.firstName" label="Nombre"
                            :error-messages="formTouched && !user.firstName ? ['Requerido'] : []" variant="outlined"
                            density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="user.lastName" label="Apellido"
                            :error-messages="formTouched && !user.lastName ? 'Requerido' : ''" variant="outlined"
                            density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="rut" label="Rut"
                            @input="rutError = !rut ? 'Rut requerido' : validationRut(rut) ? '' : 'Rut inválido'"
                            :error-messages="rutError ? [rutError] : []" variant="outlined" density="comfortable"
                            bg-color="white" />
                          <div v-if="rut && !rutError" class="text-caption text-success mt-n3 ml-2">
                            Rut válido
                          </div>
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="user.email" label="Email"
                            :error-messages="formTouched && emailError ? [emailError] : []"
                            @blur="emailError = !user.email ? 'Email requerido' : (!validateEmail(user.email) ? 'Email no válido' : '')"
                            variant="outlined" density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-select
                            v-model="user.companyId"
                            label="Empresa *"
                            :items="companies"
                            item-title="name"
                            item-value="id"
                            :error-messages="formTouched && !user.companyId ? ['Empresa requerida'] : []"
                            variant="outlined"
                            density="comfortable"
                            bg-color="white"
                            hint="Seleccione la empresa a la que pertenece el cliente"
                            persistent-hint
                          >
                            <template #item="{ props, item }">
                              <v-list-item v-bind="props" :title="item.raw.name" :subtitle="item.raw.code">
                                <template #prepend>
                                  <v-avatar size="32" color="primary">
                                    <span class="text-white text-caption font-weight-bold">
                                      {{ item.raw.code?.substring(0, 2).toUpperCase() }}
                                    </span>
                                  </v-avatar>
                                </template>
                              </v-list-item>
                            </template>
                          </v-select>
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="password" label="Contraseña" :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword"
                            :error-messages="formTouched && passwordError ? passwordError : ''" variant="outlined"
                            density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="confirmPassword" label="Repetir contraseña"
                            :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword"
                            :error-messages="formTouched && passwordError ? passwordError : ''" variant="outlined"
                            density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="12" class="mb-3">
                          <v-checkbox label="Generar contraseña aleatoria" v-model="generateRandomPassword" hide-details
                            class="mt-2 mb-4"></v-checkbox>
                        </v-col>

                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field 
                            v-model="user.folderName" 
                            label="Nombre de carpeta *"
                            :error-messages="formTouched && !user.folderName ? ['Nombre de carpeta requerido'] : []" 
                            variant="outlined"
                            density="comfortable" 
                            bg-color="white"
                            hint="Nombre único para la carpeta del usuario (sin espacios ni caracteres especiales)"
                            persistent-hint
                          />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-checkbox 
                            label="Enviar correo con contraseña" 
                            v-model="sendEmailPassword"
                            hide-details 
                            class="mt-2"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </div>
                  </v-form>
                  <!-- Remaining form sections would go here -->
                </v-sheet>
              </v-col>
            </v-row>


          </v-card>
        </div>
      </v-col>
    </v-row>
  </v-card>
  <v-snackbar v-model="activeAlert" :timeout="2000" :color="colorAlert">
    {{ textAlert }}

    <template v-slot:actions>
      <v-btn variant="text" @click="activeAlert = false">
        Cerrar
      </v-btn>
    </template>
  </v-snackbar>
</template>
<style scoped>
::-webkit-scrollbar {
  display: none;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 100;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.content-container {
  max-height: calc(100vh - 150px);
  /* o lo que se ajuste a tu layout */
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
</style>